import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { format } from "date-fns";
import { ArrowUpRight, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  LOAD_ORGANIZATIONS,
  UPDATE_ORGANIZATION_PAGE_NUMBER,
  GET_AM_ORGANIZATION
} from "constants/actions";

import SearchBar from "./SearchBar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "components/ui/pagination";
import { Button } from "components/ui/button";
import { Skeleton } from "components/ui/skeleton";

const EMPTY_STATE = [];

const List = ({ setShowHeaderTxt }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [results, setResults] = useState(EMPTY_STATE);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch({
      type: LOAD_ORGANIZATIONS,
      payload: `page=${page}${searchTerm ? `&search=${searchTerm}` : ""}`,
    });
    setLoading(true);
  }, [dispatch, page, searchTerm]);

  const { get_organizations } = useSelector(
    (state) => ({
      get_organizations: state.organizations,
    }),
    shallowEqual
  );

  useEffect(() => {
    setShowHeaderTxt(true);
  }, [setShowHeaderTxt]);

  useEffect(() => {
    const pageResults = get_organizations?.organizations?.results ?? EMPTY_STATE;
    const pageNumber = get_organizations?.pageNumber ?? 1;
    const count = get_organizations?.organizations?.count ?? 0;
    const pageSize = Math.max(1, Number(get_organizations?.organizations?.page_size) || 1);

    setResults(pageResults);
    setPage(pageNumber);
    setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
  }, [get_organizations]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(Boolean(get_organizations?.pending));
    }, 300);
    return () => clearTimeout(timer);
  }, [get_organizations]);

  const formattedRows = useMemo(
    () =>
      (results ?? EMPTY_STATE).map((organization) => ({
        id: organization?.id ?? "",
        name: organization?.name ?? "",
        createdAt: organization?.created_at
          ? format(new Date(organization.created_at), "PP")
          : "—",
        original: organization,
      })),
    [results]
  );

  const paginationRange = useMemo(() => {
    if (!totalPages || totalPages <= 1) {
      return [1];
    }

    const range = [];
    const delta = 1;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    range.push(1);

    if (left > 2) {
      range.push("ellipsis-left");
    }

    for (let number = left; number <= right; number += 1) {
      range.push(number);
    }

    if (right < totalPages - 1) {
      range.push("ellipsis-right");
    }

    if (totalPages > 1) {
      range.push(totalPages);
    }

    return range;
  }, [page, totalPages]);

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage);
    dispatch({
      type: UPDATE_ORGANIZATION_PAGE_NUMBER,
      payload: nextPage,
    });
  }, [dispatch]);

  const handleMoreDetails = useCallback((row) => {
    if (!row?.id) {
      return;
    }
    dispatch({
      type: GET_AM_ORGANIZATION,
      payload: row,
    });
    navigate(`/app/organizations/details/${row.id}`);
  }, [dispatch, navigate]);

  return (
    <div className="flex min-h-[70vh] w-full flex-col gap-6 px-6 py-6">
      <Card className="border-none bg-transparent shadow-none">
        <CardContent className="px-0">
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg">Organizations</CardTitle>
              <p className="text-sm text-muted-foreground">
                {loading ? "Fetching organizations..." : `${results?.length ?? 0} result${(results?.length ?? 0) === 1 ? "" : "s"}`}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0">
          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[120px]">ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-[180px]">Created</TableHead>
                  <TableHead className="w-[140px] text-right">More Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`} className="hover:bg-transparent">
                      <TableCell>
                        <Skeleton className="h-5 w-16" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-48" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-24" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="inline-block h-8 w-24" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : formattedRows?.length ? (
                  formattedRows.map((row) => (
                    <TableRow key={row.id || `row-${row.name}`}>
                      <TableCell className="font-medium">{row.id}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.createdAt}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleMoreDetails(row.original)}
                        >
                          View
                          <ArrowUpRight className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                      {searchTerm
                        ? "No organizations match your search."
                        : "No organizations available."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="mt-auto flex justify-end">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  if (page > 1) {
                    handlePageChange(page - 1);
                  }
                }}
                className={page <= 1 ? "pointer-events-none opacity-50" : undefined}
              />
            </PaginationItem>

            {paginationRange.map((item) => {
              if (typeof item === "string") {
                return (
                  <PaginationItem key={item}>
                    <PaginationEllipsis />
                  </PaginationItem>
                );
              }

              return (
                <PaginationItem key={`page-${item}`}>
                  <PaginationLink
                    href="#"
                    isActive={item === page}
                    onClick={(event) => {
                      event.preventDefault();
                      handlePageChange(item);
                    }}
                  >
                    {item}
                  </PaginationLink>
                </PaginationItem>
              );
            })}

            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  if (page < totalPages) {
                    handlePageChange(page + 1);
                  }
                }}
                className={page >= totalPages ? "pointer-events-none opacity-50" : undefined}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
};

export default List;